import { cn } from "@base/lib/utils";
import { ChevronRight } from "lucide-react";

const ELLIPSIS = "ellipsis" as const;

type PageItem = number | typeof ELLIPSIS;

/**
 * 현재 페이지 주변과 양 끝을 남기고 나머지는 생략 기호로 접는다.
 * 예: 총 6쪽에서 1쪽이면 [1, 2, 3, ellipsis, 6]
 */
function buildPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: PageItem[] = [];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  items.push(1);
  if (start > 2) items.push(ELLIPSIS);
  for (let page = start; page <= end; page++) items.push(page);
  if (end < totalPages - 1) items.push(ELLIPSIS);
  items.push(totalPages);

  return items;
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const Pagination = ({ currentPage, totalPages, onPageChange, className }: PaginationProps) => {
  if (totalPages <= 1) return null;

  const items = buildPageItems(currentPage, totalPages);

  return (
    <nav className={cn("flex gap-1 items-center", className)}>
      {items.map((item, index) =>
        item === ELLIPSIS ? (
          <span
            // 생략 기호는 값이 같을 수 있어 위치를 키에 포함한다.
            // biome-ignore lint/suspicious/noArrayIndexKey: 생략 기호는 값이 중복될 수 있어 위치를 키에 포함한다.
            key={`${ELLIPSIS}-${index}`}
            className="flex justify-center items-center w-8 h-8 text-gray-500 text-label2"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-current={item === currentPage ? "page" : undefined}
            onClick={() => onPageChange(item)}
            className={cn(
              "flex justify-center items-center w-8 h-8 rounded-circle text-label2 transition-colors",
              item === currentPage ? "bg-primary-500 text-white" : "text-gray-900 hover:bg-gray-100",
            )}
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        className="flex justify-center items-center w-8 h-8 text-gray-900 rounded-circle hover:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-transparent"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
};
